'use client';

import Link from 'next/link';
import Image from 'next/image';
import { Menu, X, CircleUser, ChevronDown } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { LanguageSwitcher } from './LanguageSwitcher';
import { clearCurrentUserCache, getCurrentUser } from '@/lib/clientAuth';

interface UserData {
  id: string;
  name: string;
  mail: string;
}

export function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [user, setUser] = useState<UserData | null>(null);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);

  const t = useTranslations('nav');
  const router = useRouter();

  useEffect(() => {
    let isMounted = true;

    getCurrentUser()
      .then((currentUser) => {
        if (isMounted) {
          setUser(currentUser);
        }
      })
      .catch(() => {
        if (isMounted) {
          setUser(null);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setProfileDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    clearCurrentUserCache();
    setUser(null);
    setProfileDropdownOpen(false);
    setMobileMenuOpen(false);
    router.push('/');
  };

  const navItems = [
    { href: '/', label: t('home') },
    { href: '/comment-ca-marche', label: t('howItWorks') },
  ];

  const trailingNavItems = [{ href: '/contact', label: t('contact') }];

  const solutionItems = [
    { href: '/safetizen', label: t('safetizen') },
    { href: '/earit', label: t('earit') },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-accent/30 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center h-16">
          {/* Left: Logo */}
          <div className="flex-1 flex items-center">
            <Link href="/" className="flex items-center gap-3">
              <Image
                src="/images/Safetizen_logo_high_quality.png"
                alt="Safetizen"
                width={40}
                height={42}
                className="h-10 w-auto"
              />
              <span className="text-xl font-bold text-foreground hidden sm:inline">
                Safetizen
              </span>
            </Link>
          </div>

          {/* Center: Nav links (desktop) */}
          <div className="hidden md:flex flex-1 justify-center">
            <div className="flex items-center gap-8">
              {navItems.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  className="text-foreground hover:text-accent transition-colors font-medium whitespace-nowrap"
                >
                  {item.label}
                </Link>
              ))}
              <div className="relative group">
                <button
                  type="button"
                  className="inline-flex items-center gap-1 text-foreground hover:text-accent transition-colors font-medium whitespace-nowrap"
                  aria-haspopup="true"
                >
                  {t('solutions')}
                  <ChevronDown size={16} />
                </button>
                <div className="absolute left-0 top-full pt-2 hidden group-hover:block group-focus-within:block">
                  <div className="min-w-44 rounded-lg border border-accent/20 bg-white shadow-lg py-2">
                    {solutionItems.map((item) => (
                      <Link
                        key={item.href}
                        href={item.href}
                        className="block px-4 py-2 text-foreground hover:bg-accent/10 hover:text-accent transition-colors"
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
              {trailingNavItems.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  className="text-foreground hover:text-accent transition-colors font-medium whitespace-nowrap"
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Right: Profile/Login + Language switcher */}
          <div className="flex-1 hidden md:flex items-center justify-end gap-4">
            {user ? (
              <div className="relative" ref={profileRef}>
                <button
                  type="button"
                  onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                  className="group flex items-center justify-center w-10 h-10 rounded-full text-foreground hover:text-accent hover:bg-accent/10 transition-colors"
                  aria-label="Account menu"
                  title={user.mail}
                >
                  <CircleUser size={28} />
                  <span className="sr-only">{user.mail}</span>
                </button>
                {profileDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 py-2 bg-white rounded-lg shadow-lg border border-accent/20 z-50">
                    <div className="px-4 py-2 border-b border-accent/20">
                      <p className="text-sm text-foreground truncate" title={user.mail}>
                        {user.mail}
                      </p>
                    </div>
                    <Link
                      href="/espace-personnel"
                      className="block px-4 py-2 text-sm text-foreground hover:bg-accent/10 hover:text-accent"
                      onClick={() => setProfileDropdownOpen(false)}
                    >
                      {t('account')}
                    </Link>
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 text-sm text-foreground hover:bg-accent/10 hover:text-accent"
                    >
                      {t('deconnexion')}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                href="/connexion"
                className="text-foreground hover:text-accent transition-colors font-medium"
              >
                {t('signIn')}
              </Link>
            )}
            <LanguageSwitcher />
          </div>

          {/* Mobile: Language + Menu button */}
          <div className="md:hidden flex items-center gap-4 ml-auto">
            <LanguageSwitcher />
            <button
              className="text-foreground"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden pb-4 space-y-4 bg-white rounded-lg mt-2 p-4">
            {navItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="block text-foreground hover:text-accent transition-colors font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                {item.label}
              </Link>
            ))}
            <div className="space-y-2">
              <p className="text-foreground font-medium">{t('solutions')}</p>
              <div className="pl-4 space-y-2">
                {solutionItems.map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    className="block text-foreground hover:text-accent transition-colors font-medium"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
            {trailingNavItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="block text-foreground hover:text-accent transition-colors font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                {item.label}
              </Link>
            ))}
            {user ? (
              <>
                <div className="py-2 border-t border-accent/20">
                  <p className="text-sm text-foreground truncate">{user.mail}</p>
                </div>
                <Link
                  href="/espace-personnel"
                  className="block text-foreground hover:text-accent transition-colors font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t('account')}
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    handleLogout();
                  }}
                  className="block w-full text-left text-foreground hover:text-accent transition-colors font-medium"
                >
                  {t('deconnexion')}
                </button>
              </>
            ) : (
              <Link
                href="/connexion"
                className="block text-foreground hover:text-accent transition-colors font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('signIn')}
              </Link>
            )}
          </div>
        )}
      </div>
    </nav>
  );
}
